<template lang="pug">
#overlay_image_preview_wrap
  .preview-stack
    .device-panel(v-if="show_pc")
      .device-label
        span
          i.fas.fa-desktop.mr-1
          | PC
        span.text-muted {{ pcSizeLabel }}
      .pc.pc-overlay-preview.position-relative.text-center(ref="pcPopup")
        img.mx-auto.pc-imac-bg(ref="pcImacBg", src="/img/imac_bg.jpg", alt="", @load="updatePcScreenMetrics")
        .pc-preview-screen(ref="pcPreviewScreen")
          .overlay_preview(
            v-if="hasPcPreviewContent",
            :class="{ 'position_rb': position_rb }",
            :style="pcOverlayPreviewStyle"
          )
            .wrap(:style="pcOverlayWrapStyle")
              .del(v-if="close_btn_flag")
                img(src="/img/close_overlay.png?v2")
              img.overlay_img(:src="pcImageSrc", :key="'pc-' + pcImageSrc")
          .preview-empty.preview-empty--in-screen(v-else)
            | PC用画像が登録されていません

    .device-panel(v-if="show_sp")
      .device-label
        span
          i.fas.fa-mobile-alt.mr-1
          | スマートフォン
        span.text-muted 自動
      .iphone-fit-wrap(ref="spFitWrap", :style="spWrapStyle")
        .iphone-scale-inner(:style="spScaleInnerStyle")
          .iphone
            .iphone-top
              span.camera
              span.sensor
              span.speaker
            .top-bar
            .iphone-screen
              .overlay_preview(v-if="hasSpPreviewContent", :class="{ 'position_rb': position_rb }")
                .wrap(:style="spOverlayWrapStyle")
                  .del(v-if="close_btn_flag")
                    img(src="/img/close_overlay.png?v2")
                  img.overlay_img(:src="spImageSrc", :key="'sp-' + spImageSrc")
              .preview-empty.preview-empty--in-phone(v-else)
                | スマートフォン用画像が登録されていません
            .buttons
              span.on-off
              span.sleep
              span.up
              span.down
            .bottom-bar
            .iphone-bottom
              span
</template>

<script>
import Vue from 'vue';
import {
  mapSpColumnWidthToScale,
  PC_PREVIEW_SCREEN_WIDTH_RATIO,
  PC_PREVIEW_SCREEN_HEIGHT_RATIO,
  SP_PREVIEW_DESIGN_HEIGHT,
  SP_PREVIEW_DESIGN_WIDTH,
} from '../utils/preview_size';

const SP_PREVIEW_TOP_PADDING = 8;
const SP_PREVIEW_BOTTOM_PADDING = 8;
const SP_PREVIEW_WRAP_MARGIN_TOP = 0;
/** 画面下オーバーレイ: プレビュー画面幅に対する比率（やや控えめ） */
const PC_BOTTOM_OVERLAY_SCREEN_RATIO = 0.25;
/**
 * 右下オーバーレイ: 旧プレビューは max_width*(3/7)（imac幅700px前提）。
 * 現在の画面幅へ換算する基準画面幅 = 700 * PC_PREVIEW_SCREEN_WIDTH_RATIO
 */
const PC_RB_OVERLAY_REFERENCE_IMAC_WIDTH = 700;
const PC_RB_OVERLAY_LEGACY_SCALE = 3 / 7;
/** 実配信のデフォルト width: 30% に合わせる */
const PC_RB_OVERLAY_DEFAULT_SCREEN_RATIO = 0.3;
const SP_RB_OVERLAY_SIZE = 80;

export default {
  props: {
    position_rb: { type: Boolean, default: false },
  },
  data() {
    return {
      spScale: 0.7,
      spResizeObserver: null,
      pcScreenWidth: 0,
      pcScreenHeight: 0,
    };
  },
  methods: {
    updatePreviewLayout() {
      this.updatePcScreenMetrics();
      this.$nextTick(() => {
        this.updateSpScale();
      });
    },
    updateSpScale() {
      const wrap = this.$refs.spFitWrap;
      if (!wrap) {
        return;
      }
      const availableWidth = Math.max(wrap.clientWidth - 8, 0);
      if (!availableWidth) {
        return;
      }
      this.spScale = mapSpColumnWidthToScale(availableWidth);
    },
    updatePcScreenMetrics() {
      const screen = this.$refs.pcPreviewScreen;
      if (screen && screen.clientWidth && screen.clientHeight) {
        this.pcScreenWidth = screen.clientWidth;
        this.pcScreenHeight = screen.clientHeight;
        return;
      }

      const imac = this.$refs.pcImacBg;
      if (!imac || !imac.clientWidth || !imac.clientHeight) {
        return;
      }
      this.pcScreenWidth = Math.round(imac.clientWidth * PC_PREVIEW_SCREEN_WIDTH_RATIO);
      this.pcScreenHeight = Math.round(imac.clientHeight * PC_PREVIEW_SCREEN_HEIGHT_RATIO);
    },
    resolvePcRbOverlayWidth(maxWidth, isPercent) {
      const screenWidth = this.pcScreenWidth || 360;

      if (isPercent) {
        const ratio = parseFloat(maxWidth) / 100;
        if (!Number.isFinite(ratio) || ratio <= 0) {
          return Math.round(screenWidth * PC_RB_OVERLAY_DEFAULT_SCREEN_RATIO);
        }
        return Math.round(screenWidth * ratio);
      }

      const pixelWidth = typeof maxWidth === 'number' ? maxWidth : parseInt(maxWidth, 10);
      if (!pixelWidth) {
        return Math.round(screenWidth * PC_RB_OVERLAY_DEFAULT_SCREEN_RATIO);
      }

      // 旧: max_width*(3/7) @ imac700 → 現在の画面幅比率へスケール
      const referenceScreenWidth = PC_RB_OVERLAY_REFERENCE_IMAC_WIDTH * PC_PREVIEW_SCREEN_WIDTH_RATIO;
      const legacyPreviewWidth = pixelWidth * PC_RB_OVERLAY_LEGACY_SCALE;
      return Math.round(legacyPreviewWidth * screenWidth / referenceScreenWidth);
    },
  },
  watch: {
    show_sp(val) {
      if (val) {
        this.$nextTick(() => {
          this.updatePreviewLayout();
        });
      }
    },
    show_pc(val) {
      if (val) {
        this.$nextTick(() => {
          this.updatePcScreenMetrics();
        });
      }
    },
    hasPcPreviewContent(val) {
      if (val) {
        this.$nextTick(() => {
          this.updatePcScreenMetrics();
        });
      }
    },
    position_rb() {
      this.$nextTick(() => {
        this.updatePreviewLayout();
      });
    },
  },
  computed: {
    spWrapStyle() {
      const scaledHeight = Math.ceil(SP_PREVIEW_DESIGN_HEIGHT * this.spScale);
      const shadowPad = 12;

      return {
        height: `${scaledHeight + SP_PREVIEW_TOP_PADDING + SP_PREVIEW_BOTTOM_PADDING + shadowPad}px`,
        paddingTop: `${SP_PREVIEW_TOP_PADDING}px`,
        paddingBottom: `${SP_PREVIEW_BOTTOM_PADDING}px`,
        marginTop: `${SP_PREVIEW_WRAP_MARGIN_TOP}px`,
        width: '100%',
        overflowX: 'hidden',
        overflowY: 'visible',
        boxSizing: 'border-box',
      };
    },
    spScaleInnerStyle() {
      const scale = this.spScale;
      const widthInset = Math.round(SP_PREVIEW_DESIGN_WIDTH * (scale - 1) / 2);

      return {
        transform: `scale(${scale})`,
        transformOrigin: 'top center',
        width: `${SP_PREVIEW_DESIGN_WIDTH}px`,
        height: `${SP_PREVIEW_DESIGN_HEIGHT}px`,
        marginBottom: `${Math.round(SP_PREVIEW_DESIGN_HEIGHT * (scale - 1))}px`,
        marginLeft: `${widthInset}px`,
        marginRight: `${widthInset}px`,
      };
    },
    is_upload_image() {
      return this.$store.state.creative.preview_list.is_upload_image;
    },
    is_upload_pc_image() {
      return this.$store.state.creative.preview_list.is_upload_pc_image;
    },
    rawSpUploadPath() {
      return this.$store.state.creative.preview_upload_image_path || '';
    },
    rawSpImage() {
      return this.$store.state.creative.preview_list.image || '';
    },
    rawPcUploadPath() {
      return this.$store.state.creative.preview_upload_pc_image_path || '';
    },
    rawPcImage() {
      return this.$store.state.creative.preview_list.pc_image || '';
    },
    spImageSrc() {
      if (this.is_upload_image == 1) {
        return this.rawSpUploadPath || this.rawSpImage || null;
      }
      if (this.is_upload_image == 0) {
        return this.rawSpImage || null;
      }
      return null;
    },
    pcImageSrc() {
      if (this.is_upload_pc_image == 1) {
        return this.rawPcUploadPath || this.rawPcImage || null;
      }
      if (this.is_upload_pc_image == 0) {
        return this.rawPcImage || null;
      }
      return null;
    },
    close_btn_flag() {
      return this.$store.state.creative.preview_list.close_btn_flag;
    },
    show_pc() {
      return this.$store.state.creative.preview_list.show_pc;
    },
    show_sp() {
      return this.$store.state.creative.preview_list.show_sp;
    },
    maxWidthSetting() {
      return this.$store.state.creative.preview_list.max_width;
    },
    isPercentMaxWidth() {
      return typeof this.maxWidthSetting === 'string' && this.maxWidthSetting.includes('%');
    },
    pcSizeLabel() {
      if (!this.position_rb) {
        return '自動';
      }
      const maxWidth = this.maxWidthSetting;
      if (maxWidth === undefined || maxWidth === null || maxWidth === '') {
        return '';
      }
      if (this.isPercentMaxWidth) {
        return maxWidth;
      }
      return maxWidth + 'px';
    },
    pcOverlayPreviewStyle() {
      if (!this.position_rb) {
        return {};
      }
      const inset = Math.max(8, Math.round((this.pcScreenWidth || 360) * 0.025));
      return {
        '--overlay-screen-inset': inset + 'px',
      };
    },
    pcOverlayWrapStyle() {
      const screenWidth = this.pcScreenWidth || 360;

      if (!this.position_rb) {
        const width = Math.round(screenWidth * PC_BOTTOM_OVERLAY_SCREEN_RATIO);
        return {
          width: width + 'px',
          maxWidth: '90%',
          '--overlay-preview-width': width + 'px',
        };
      }

      const previewWidth = this.resolvePcRbOverlayWidth(this.maxWidthSetting, this.isPercentMaxWidth);
      return {
        '--overlay-preview-width': previewWidth + 'px',
      };
    },
    spOverlayWrapStyle() {
      if (!this.position_rb) {
        return {
          width: '100%',
        };
      }
      return {
        width: 'fit-content',
        '--overlay-preview-width': SP_RB_OVERLAY_SIZE + 'px',
      };
    },
    hasPcPreviewContent() {
      return !!this.pcImageSrc;
    },
    hasSpPreviewContent() {
      return !!this.spImageSrc;
    },
  },
  async mounted() {
    await Vue.nextTick();
    this.updatePreviewLayout();
    if (typeof ResizeObserver !== 'undefined') {
      const observeTarget = this.$el.closest('#previe_area') || this.$el;
      this.spResizeObserver = new ResizeObserver(() => {
        this.updatePreviewLayout();
      });
      this.spResizeObserver.observe(observeTarget);
    } else {
      window.addEventListener('resize', this.updatePreviewLayout);
    }
  },
  beforeDestroy() {
    if (this.spResizeObserver) {
      this.spResizeObserver.disconnect();
      this.spResizeObserver = null;
    } else {
      window.removeEventListener('resize', this.updatePreviewLayout);
    }
  },
};
</script>

<style lang="scss" scoped>
.preview-stack {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  align-items: start;
}
.device-panel {
  border: 1px solid #dfe5ec;
  border-radius: 9px;
  padding: 10px;
  background: #f8fafc;
  overflow: visible;
}
.device-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 700;
}
.preview-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  padding: 16px;
  color: #6b7280;
  font-size: 12px;
  text-align: center;
  background: #eef1f5;
  border-radius: 6px;
  border: 1px dashed #c9d3df;
}
.preview-empty--in-phone,
.preview-empty--in-screen {
  position: absolute;
  inset: 0;
  min-height: 0;
  border-radius: 0;
  z-index: 2;
}
</style>
